import { reactive } from 'vue'

import useHeartrate from './heartrate'
import useFreejump from './freejump'
import useReinforces from './reinforces'
import useSerial from './serial'
import useBluetooth from './bluetooth'
import useBluetoothNative from './bluetoothNative'

let language = 'en'
let handlers = {}
let sharedReinforcesLeft = null
let sharedReinforcesRight = null

const triggerEvent = (...args) => {
    const [eventName, ...params] = args
    const handler = handlers[eventName]
    if (typeof handler === 'function') {
        handler(...params)
    } else {
        console.warn('[Sensor] Unhandled event:', eventName)
    }
}

export default {
    install(app) {
        const sensorFreejump = useFreejump({ triggerEvent, language })
        const reinforcesLeft = sharedReinforcesLeft || useReinforces({ triggerEvent, language })
        const reinforcesRight = sharedReinforcesRight || useReinforces({ triggerEvent, language })
        sharedReinforcesLeft = reinforcesLeft
        sharedReinforcesRight = reinforcesRight
        const sensorHeartrate = useHeartrate({ triggerEvent, language })
        const sensorSerial = useSerial({ triggerEvent, language })
        const sensorBluetooth = useBluetooth({
            triggerEvent,
            language,
            heartrate: sensorHeartrate,
            freejump: sensorFreejump,
            reinforces: reinforcesLeft,
            reinforcesDevices: {
                left: reinforcesLeft,
                right: reinforcesRight,
            },
        })
        const sensorBluetoothNative = useBluetoothNative({
            triggerEvent,
            language,
            bluetooth: sensorBluetooth,
        })

        // Expose both Reinforces instances to the Bluetooth driver if supported
        try {
            sensorBluetooth.reinforcesDevices = {
                left: reinforcesLeft,
                right: reinforcesRight,
            }
            if (typeof sensorBluetooth.setReinforcesTargets === 'function') {
                sensorBluetooth.setReinforcesTargets({
                    left: reinforcesLeft,
                    right: reinforcesRight,
                })
            }
        } catch {}

        const movepro = reactive({
            current: sensorBluetooth,
            bluetooth: sensorBluetooth,
            bluetoothNative: sensorBluetoothNative,
            serial: sensorSerial,
        })

        app.config.globalProperties.$heartrate = sensorHeartrate
        app.config.globalProperties.$freejump = sensorFreejump
        app.config.globalProperties.$movepro = movepro
        // Dual-device helpers
        const syncTimeBoth = async () => {
            try {
                await reinforcesLeft.syncTime?.()
            } catch {}
            try {
                await reinforcesRight.syncTime?.()
            } catch {}
        }
        const calibrateBoth = async () => {
            try {
                await reinforcesLeft.calibrate?.()
            } catch {}
            try {
                await reinforcesRight.calibrate?.()
            } catch {}
        }
        const startRecordingBoth = async () => {
            try {
                await reinforcesLeft.startRecording?.()
            } catch {}
            try {
                await reinforcesRight.startRecording?.()
            } catch {}
        }
        const stopBoth = async () => {
            try {
                await reinforcesLeft.stopRecording?.()
            } catch {}
            try {
                await reinforcesLeft.stopStreaming?.()
            } catch {}
            try {
                await reinforcesRight.stopRecording?.()
            } catch {}
            try {
                await reinforcesRight.stopStreaming?.()
            } catch {}
        }
        app.provide('heartrate', app.config.globalProperties.$heartrate)
        app.provide('freejump', app.config.globalProperties.$freejump)
        app.provide('reinforcesLeft', reinforcesLeft)
        app.provide('reinforcesRight', reinforcesRight)
        app.provide('reinforcesBoth', { syncTimeBoth, calibrateBoth, startRecordingBoth, stopBoth })
        app.provide('movepro', movepro)
        app.provide('setMoveProDriver', (driver) => {
            if (movepro[driver]) {
                movepro.current = movepro[driver]
            } else {
                console.warn(`[MovePro] Unknown driver: ${driver}`)
                movepro.current = movepro.bluetooth
            }
        })
    },

    configure(config) {
        language = config.language || language

        if (config.handlers) {
            handlers = { ...handlers, ...config.handlers }
        }
    },
}
